import camelCase from 'camelcase';
import { PimcoreLayoutInput, PimcoreLayoutPanel } from '@/types/entity';
import { CollectionsItemOption, FieldComponent, FormFieldElement } from '@/types/FieldComponent';

/**
 * <pre>
 * Функция преобразует строчные стили в js объект
 * input: font-size: 16px; color: #fff; margin-right: 3px;
 * output: {
 *   fontSize: '16px',
 *   color: '#fff',
 *   marginRight: '3px'
 * }
 * </pre>
 * @param styles
 */
const convertInlineStylesToObject = (styles?: string): Record<string, any> => {
  const styleObj: Record<string, any> = {};
  if (!styles) {
    return styleObj;
  }
  styles
    .split(';')
    .forEach((style: string) => {
      const [property, value] = style.split(':');
      if (property) {
        const camelCaseProperty = camelCase(property.trim());
        styleObj[camelCaseProperty] = value.trim();
      }
    });
  return styleObj;
};

/* TODO ref */
export const findFieldComponent = (
  layoutElement: PimcoreLayoutPanel | PimcoreLayoutInput,
  fieldComponents: FieldComponent[],
): FormFieldElement => {
  const isElementPanel = layoutElement.fieldtype === 'panel';
  const fieldLabel: string = layoutElement.title;
  if (isElementPanel) {
    const fieldChildren = layoutElement.childs
      .map((childElement: PimcoreLayoutPanel | PimcoreLayoutInput) => findFieldComponent(childElement, fieldComponents))
      .filter((formField: FormFieldElement) => Boolean(formField.fieldComponent));
    return {
      fieldLabel,
      fieldChildren,
    };
  }

  const fieldComponent: FieldComponent | undefined = fieldComponents
    .find(({ name }) => name.toLocaleLowerCase() === layoutElement.name.toLocaleLowerCase());

  if (fieldComponent) {
    Object.assign(fieldComponent.attrs, {
      layoutOptions: layoutElement,
    });
  }

  const { width, style } = layoutElement;
  const pimStyles = convertInlineStylesToObject(style);
  const styles = {
    ...width ? { width: `${width}px` } : {},
    ...pimStyles,
  };

  return {
    fieldLabel,
    fieldComponent,
    fieldStyles: styles,
  };
};

const normalizeValue = async (field: FieldComponent, value: any): Promise<any> => {
  if (!field.beforeSave) return value;
  return field.beforeSave(value);
};

export const normalizeData = async (
  fieldData: Record<string, any> = {},
  fieldComponents: FieldComponent[] = [],
): Promise<Record<string, any>> => {
  const entries = Object.entries(fieldData);
  const normalizedData = entries.map(async (entry) => {
    const [key, value] = entry;
    const field = fieldComponents.find((f: FieldComponent) => f.name === key);
    const normalizedValue = field
      ? await normalizeValue(field, value)
      : value;
    return [key, normalizedValue];
  });
  return Object.fromEntries(await Promise.all(normalizedData));
};

const previewCollectionItem = (fieldComponent: FieldComponent, value: any) => {
  const label = fieldComponent.props?.['item-text'];
  const valueItem = fieldComponent.props.items.find((item: CollectionsItemOption) => parseInt(item.id, 10) === parseInt(value.id, 10));
  return valueItem
    ? valueItem[label] || value
    : value;
};

const previewDataValue = (fieldComponent: FieldComponent, value: any) => {
  if (fieldComponent.props?.items && value?.id) {
    return previewCollectionItem(fieldComponent, value);
  }
  if (fieldComponent.props?.items && Array.isArray(value)) {
    return value.map((item) => previewCollectionItem(fieldComponent, item));
  }
  return value;
};

export const normalizePreviewData = (
  fieldData: Record<string, any> = {},
  fieldComponents: FieldComponent[] = [],
): Record<string, any> => {
  let fieldDataArray = Object.entries(fieldData);
  fieldDataArray = fieldDataArray.map((entry) => {
    const [key, value] = entry;
    const fieldComponent = fieldComponents.find((f: FieldComponent) => f.name === key);
    let normalizedValue = value;
    if (
      Array.isArray(value)
      && fieldComponent?.attrs?.collection
      && fieldComponent?.attrs?.fieldComponents
    ) {
      normalizedValue = value.map((collectionItem) => normalizePreviewData(collectionItem, fieldComponent?.attrs.fieldComponents));
    } else if (fieldComponent) {
      normalizedValue = previewDataValue(fieldComponent, normalizedValue);
    }
    return [key, normalizedValue];
  });
  return Object.fromEntries(fieldDataArray);
};
